'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface TaskCardProps {
  task: {
    id: string;
    title: string;
    description: string;
    assignee: {
      name: string;
      avatar: string;
      alt: string;
    };
    dueDate: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    status: 'todo' | 'in-progress' | 'completed';
    category: string;
  };
  onStatusChange: (taskId: string, newStatus: 'todo' | 'in-progress' | 'completed') => void;
  onEdit: (taskId: string) => void;
  onDelete: (taskId: string) => void;
}

const TaskCard = ({ task, onStatusChange, onEdit, onDelete }: TaskCardProps) => {
  const [showMenu, setShowMenu] = useState(false);

  const getPriorityColor = () => {
    switch (task.priority) {
      case 'urgent':
        return 'bg-error/10 text-error border-error/20';
      case 'high':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'medium':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'low':
        return 'bg-primary/10 text-primary border-primary/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = () => {
    switch (task.status) {
      case 'completed':
        return 'CheckCircleIcon';
      case 'in-progress':
        return 'ClockIcon';
      default:
        return 'CircleStackIcon';
    }
  };

  return (
    <div className="glass p-3 lg:p-4 hover:shadow-warm-sm transition-smooth">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full overflow-hidden border-2 border-sage-200/30 flex-shrink-0">
            <AppImage
              src={task.assignee.avatar}
              alt={task.assignee.alt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-caption font-medium text-foreground mb-1 text-sm lg:text-base break-words">
              {task.title}
            </h3>
            <p className="text-xs lg:text-sm text-muted-foreground line-clamp-2 break-words">
              {task.description}
            </p>
          </div>
        </div>
        <div className="relative ml-2">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 rounded hover:bg-muted transition-smooth"
            aria-label="Task options"
          >
            <Icon
              name="EllipsisVerticalIcon"
              size={20}
              className="text-muted-foreground"
            />
          </button>
          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 top-8 w-40 bg-popover border border-border rounded-lg shadow-warm-md z-50 py-1">
                <button
                  onClick={() => {
                    onEdit(task.id);
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm font-caption hover:bg-muted transition-smooth flex items-center gap-2"
                >
                  <Icon name="PencilIcon" size={16} />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onDelete(task.id);
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm font-caption hover:bg-muted transition-smooth flex items-center gap-2 text-error"
                >
                  <Icon name="TrashIcon" size={16} />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-caption font-medium border ${getPriorityColor()}`}
        >
          <Icon name="FlagIcon" size={12} />
          {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-caption bg-muted text-muted-foreground">
          <Icon name="CalendarIcon" size={12} />
          {task.dueDate}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-caption bg-sage-50 text-foreground">
          <Icon name="TagIcon" size={12} />
          {task.category}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={() => onStatusChange(task.id, 'todo')}
          className={`flex-1 py-1.5 rounded text-xs font-caption font-medium transition-smooth ${
            task.status === 'todo'
              ? 'bg-muted text-foreground' :'text-muted-foreground hover:bg-muted/50'
          }`}
        >
          To Do
        </button>
        <button
          onClick={() => onStatusChange(task.id, 'in-progress')}
          className={`flex-1 py-1.5 rounded text-xs font-caption font-medium transition-smooth ${
            task.status === 'in-progress' ?'bg-accent/10 text-accent' :'text-muted-foreground hover:bg-muted/50'
          }`}
        >
          In Progress
        </button>
        <button
          onClick={() => onStatusChange(task.id, 'completed')}
          className={`flex-1 py-1.5 rounded text-xs font-caption font-medium transition-smooth ${
            task.status === 'completed'
              ? 'bg-success/10 text-success' :'text-muted-foreground hover:bg-muted/50'
          }`}
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default TaskCard;